import { IssueOrServiceType } from "~/app/data/statuses/chat";
import { Material, Part, SearchUser } from "~/types";

export interface WorkOrder {
    id: string;
    machineId: string;
    machineName: string;
    machineIntern: string;
    responsible: string;
    status: WorkOrderStatuses;
    taskCount: number;
    workOrderNo: string;
    workOrderStartDate: string;
    workOrderEndDate: string;
    createdAt: string;
    updatedAt: string;
}

export interface WorkOrderDetailResponse {
    success: boolean;
    data: WorkOrderDetail;
}

export interface WorkOrderDetail {
    id: number;
    history: WorkOrderHistory[];
    workOrderNo: string;
    lastUpdated: {
        date: string | null;
        name: string | null;
    },
    status: number;
    statusInfo: {
        comment: string | null;
        statusAt: string | null;
        statusBy: string | null;
    },
    isStarted: 0 | 1;
    excavator: Excavator;
    operatingTime: number | null;
    operatingCount: number | null;
    createdBy: UserReference;
    approvedBy: UserReference;
    personnel: UserReference;
    mechanics: WorkOrderMechanic[];
    workType: number;
    repairLocation: string;
    costCategoryId: number | null;
    departmentId: number | null;
    departmentPrefix?: string | null;
    chargedDepartmentId: number | null;
    customerId: number | null;
    projectId: number | null;
    sortingType: number | null;
    invoiceAcceptanceUser: string | null;
    invoiceAcceptanceUserId: number | null;
    scheduledStartDate: string;
    scheduledEndDate: string;
    actualStartDate: string | null;
    actualEndDate: string | null;
    description: string;
    comment: string;
    attachments: Attachment[];
    tasks: WorkOrderTask[];
    summary: WorkOrderSummary;
    parts: WorkOrderPartList[];
    materials: Material[];
}

export interface WorkOrderSummary {
    totalHours: number;
    totalParts: number;
    totalMaterials: number;
}

export interface WorkOrderTask {
    id: number;
    title: string;
    type: IssueOrServiceType;
    status: WorkOrderTaskStatus;
}

export interface WorkOrderTaskChecklist {
    completed: number;
    total: number;
}

export interface WorkOrderTaskDetail {
    id: number;
    title: string;
    type: IssueOrServiceType;
    status: WorkOrderTaskIssueStatus | WorkOrderTaskServiceStatus;
    description: string;
    users: WorkOrderTaskUserList[];
    parts: WorkOrderTaskPartList[];
    materials: WorkOrderTaskMaterialList[];
    attachments: Attachment[];
    checklist?: WorkOrderTaskChecklist;
}

export interface WorkOrderTaskDetailPayload {
    id: number;
    taskId: number;
    type: IssueOrServiceType;
    showLoading?: boolean;
}

export interface WorkOrderTaskDetailResponse {
    success: boolean;
    data: WorkOrderTaskDetail;
}

export interface WorkOrderTaskMaterialList {
    id: number;
    name: string;
    quantity: number;
    type: IssueOrServiceType;
    issueId: number;
}

export interface WorkOrderTaskPartList {
    id: number;
    name: string;
    quantity: number;
    type: IssueOrServiceType;
    issueId: number;
}

export interface WorkOrderPartList extends Part {
    fromType?: 0 | 1,
    issueId?: number;
}

export interface WorkOrderTaskUserList {
    id: number;
    name: string;
    ordinaryHours: number;
    overTime50: number;
    overTime100: number;
    hoursLogDate: string;
    comment?: string;
    workType?: number;
    hpCode?: number;
}

export interface WorkOrderTimeEntryPayload {
    id?: number;
    workOrderId: number;
    taskId: number;
    type: IssueOrServiceType;
    comment?: string;
    hpCode?: number;
    workType: number;
    ordinaryHours: number | string;
    overTime50?: number | string;
    overTime100?: number | string;
}

export interface WorkOrderTimeEntryResponse {
    success: boolean;
    message?: string;
    customMessage?: string;
}

export enum WorkOrderTaskStatus {
    INCOMPLETE = 0,
    COMPLETED = 1,
    REMOVED = 2,
}

export enum WorkOrderTaskIssueStatus {
    REGISTERED = 0,
    DURING_WORK = 1,
    COMPLETED = 2,
    CLOSED = 3,
}

export enum WorkOrderTaskServiceStatus {
    PREVIOUS = 0,
    TRIGGED = 1,
    DURING_WORK = 2,
    COMPLETED = 3,
    PARTIALLY_COMPLETED = 4,
    CANCELLED = 5,
}

export interface WorkOrderHistory {
    id: number;
    user: string;
    status: number;
    type: number;
    logValue: string | null;
    createdAt: string;
}

export interface Excavator {
    id: number;
    name: string;
    intern: string;
    isOperatingTimeVisible: number;
    operatingTimeAllowed: number;
    isOperatingCountVisible: number;
    operatingCountAllowed: number;
    tracker?: number | null;
}

export interface UserReference {
    id: number;
    name: string;
}

export interface WorkOrderMechanic {
    id: number;
    name: string;
    mechanicId: number;
    workOrderId: number;
}

export interface WorkOrderMechanicPayload {
    id: number;
    workOrderId: number;
    mechanicId: number;
    operation: 'add' | 'remove';
}

export interface WorkOrderMechanicResponse {
    success: boolean;
    message?: string;
    data?: WorkOrderMechanic;
}

export interface Attachment {
    id: number;
    archiveDocId: number;
    name: string;
    url: string;
    mimeType: string;
}

export interface SaveWorkOrderAttachmentPayload {
    workOrderId: number;
    files: File[];
}

export interface DeleteWorkOrderAttachmentPayload {
    id: number;
    workOrderId: number;
}

export interface SaveIssueServiceAttachmentPayload {
    id: number;
    type: IssueOrServiceType;
    files: File[];
}

export interface DeleteIssueServiceAttachmentPayload {
    id: number;
    type: IssueOrServiceType;
    url: string;
}

export interface SaveIssueServiceAttachmentResponse {
    success: boolean;
    message?: string;
}

export interface WorkOrderAttachmentResponse {
    success: boolean;
    message?: string;
}

export interface WorkOrderHistoryPayload {
    offset: number;
    machineId: string | null;
    filters: FilterWorkOrderPayload;
}

export interface WorkOrderHistoryResponse {
    data: WorkOrder[],
    success: boolean;
    message: string;
}

export interface WorkOrderGeneralSettings {
    hoursCount: number;
    plannedStartDate: string;
    plannedEndDate: string;
    setLiveOperatingHours: boolean;
    allowManualOperatingTimeUpdate: boolean;
}

export type WorkOrderAutoSaveFieldKey =
    | 'operatingTime'
    | 'operatingCount'
    | 'comment'
    | 'description'
    | 'scheduledStartDate'
    | 'scheduledEndDate'
    | 'issueStatus';

export interface WorkOrderAutoSaveDataPayload {
    key: WorkOrderAutoSaveFieldKey;
    workOrderId: number;
    value: string | number;
    issueId?: number;
    userCount?: number;
}

export interface WorkOrderAutoSaveDataResponse {
    success: boolean;
    message?: string;
}

export interface SaveAdministrativeDataPayload {
    workOrderId: number;
    departmentId: number;
    personnelId?: number;
    repairLocation?: string;
    workType?: number;
    costCategoryId?: number | null;
    chargedDepartmentId?: number | null;
    customerId?: number | null;
    projectId?: number | null;
    sortingType?: number | null;
    displayWork?: number;
    changeAll?: boolean;
    invoiceAcceptanceUserId?: number | null;
}

export interface SaveAdministrativeDataResponse {
    success: boolean;
    message: string;
}

export interface WorkOrderExcavatorTaskListPayload {
    machineId: string;
    searchText: string;
    offset: number;
    types?: IssueOrServiceType[];
}

export interface WorkOrderExcavatorTaskListResponse {
    data: WorkOrderExcavatorTask[];
    success: boolean;
    message: string;
    total?: number;
}

export interface WorkOrderExcavatorTask {
    id: number;
    type: IssueOrServiceType;
    title: string;
    description: string;
    date: string;
    serviceIntervalId?: number;
}

export interface SaveWorkOrderTasksPayload {
    workOrderId: number;
    tasks: WorkOrderExcavatorTask[];
}

export interface SaveWorkOrderTasksResponse {
    success: boolean;
    message: string;
}

export interface RemoveWorkOrderTaskPayload {
    workOrderId: number;
    taskId: number;
    deleteParts: boolean;
    type: IssueOrServiceType;
}

export interface RemoveWorkOrderTaskResponse {
    success: boolean;
    message: string;
}

export interface SaveWorkOrderPayload {
    workOrderId: number;
    status: WorkOrderStatuses;
    scheduledStartDate: string;
    scheduledEndDate: string;
}

export interface SaveWorkOrderResponse {
    success: boolean;
    message: string;
}

export interface AdministrativeFormValues {
    personnelId: number;
    repairLocation: string;
    workType: number;
    costCategoryId: number | null;
    chargedDepartmentId: number | null;
    customerId: number | null;
    projectId: number | null;
    sortingType: number | null;
    invoiceAcceptanceUserId: number | null;
}

/** Matches BE Util::WORK_ORDER_SOURCE_TYPE_* */
export enum WorkOrderSourceType {
    MM_NORMAL = 1,
    MM_SIMPLIFIED = 2,
    BARDE_FELT_NORMAL = 3,
    BARDE_FELT_SIMPLIFIED = 4,
}

export interface CreateWorkOrderPayload {
    machineId: string;
    departmentId: number;
    responsibleId: number;
    plannedStartDate: string;
    plannedEndDate: string;
    description: string;
    tasks: WorkOrderExcavatorTask[];
    sourceType: WorkOrderSourceType;
}

export interface CreateWorkOrderResponse {
    success: boolean;
    message: string;
    workOrderId: string;
}

export interface WorkOrderDepartment {
    id: number;
    name: string;
    hasCostCategory: boolean
    users: SearchUser[]
};

export interface FilterWorkOrderPayload {
    searchText: string;
    type: WorkOrderHistoryPayloadType;
    departments: string[];
    status: string[];
}

export enum WorkOrderStatuses {
    OPEN = 0,
    PLANNED = 1,
    DELAYED = 2,
    INPROGRESS = 3,
    ONHOLD = 4,
    FINISHED = 5,
    APPROVED = 6,
    CANCELLED = 7,
    NOTAPPROVED = 8
}

export const WorkOrderStatusesList = [WorkOrderStatuses.OPEN, WorkOrderStatuses.PLANNED, WorkOrderStatuses.DELAYED, WorkOrderStatuses.INPROGRESS, WorkOrderStatuses.ONHOLD, WorkOrderStatuses.FINISHED, WorkOrderStatuses.APPROVED, WorkOrderStatuses.CANCELLED, WorkOrderStatuses.NOTAPPROVED];

export interface UpdateWorkOrderStatusPayload {
    workOrderId: number;
    status: WorkOrderStatuses;
}

export interface UpdateWorkOrderStatusResponse {
    success: boolean;
    message: string;
}

export interface WorkOrderFilterOption {
    id: string
    name: string
}
export interface WorkOrderFilterOptions {
    status: WorkOrderFilterOption[]
}

export enum WorkOrderHistoryPayloadType {
    MINE = 1,
    ALL = 0,
}

export interface CreateWorkOrderFormValues {
    machineId: string | number;
    departmentId: number;
    responsibleId: number;
    plannedStartDate: string;
    plannedEndDate: string;
    description: string;
}